import { useGlobalContext } from '@/modules/global-context/use-global-context';
import { useState, useEffect, useRef } from 'react';
import { Checkbox, CheckboxCheckedChangeDetails, Flex, Stack, Text, Accordion } from '@chakra-ui/react';
import { getInventoryClass } from '@/util/product-helper';
import { useRouter } from 'next/router';
import { useProductSearch } from '@/modules/product-context';
import styles from '../index.module.css';
import { identifyPage, LinkEnum } from '@/util/link-manager';
import {
  categoryMapToClassCategoryGroup,
  classCategoryGroupToMap,
  updateCategoryMap,
  updateInventoryClassMap,
} from '../category-filter-state';

export const CategoryAccordion = () => {
  const {
    meta: { categories },
  } = useGlobalContext();

  const [map, setMap] = useState<Map<string, string[]>>(new Map());
  const mapRef = useRef(map);

  const { searchParams, onSearch } = useProductSearch();

  const router = useRouter();

  const page = identifyPage(router.pathname);

  const applyCategoryMap = (nextMap: Map<string, string[]>) => {
    mapRef.current = nextMap;
    setMap(nextMap);
    onSearch({ classCategoryGroup: categoryMapToClassCategoryGroup(nextMap) });
  };

  // Click on the child category
  const onCategoryChange = (e: CheckboxCheckedChangeDetails, category: string, subCategory: string) => {
    applyCategoryMap(
      updateCategoryMap({
        categoryMap: mapRef.current,
        inventoryClass: category,
        inventoryCategory: subCategory,
        checked: e.checked === true,
      }),
    );
  };

  // Click on the parent category class
  const onClassChange = (e: CheckboxCheckedChangeDetails, category: string) => {
    applyCategoryMap(
      updateInventoryClassMap({
        categoryMap: mapRef.current,
        inventoryClass: category,
        inventoryCategories: categories?.[category]?.categories.map(({ category }) => category) ?? [],
        checked: e.checked === true,
      }),
    );
  };

  // Sync the checkbox map when searchParams changes from the URL or another filter
  useEffect(() => {
    const nextMap = classCategoryGroupToMap(searchParams.classCategoryGroup);
    mapRef.current = nextMap;
    setMap(nextMap);
  }, [searchParams]);

  return page !== LinkEnum.INVENTORY ? (
    <Stack
      gap="4"
      borderRadius={'6px'}
      border={'1px solid var(--chakra-colors-border-transparent)'}
      boxShadow={'0px 4px 6px -2px #0000000D, 0px 10px 15px -3px #0000001A'}
    >
      <Accordion.Root multiple pl={3} pr={3} py={1} pb={1}>
        {categories &&
          Object.keys(categories).map((category) => (
            <Accordion.Item key={category} value={category} borderBottom={'none'}>
              <Accordion.ItemTrigger cursor={'pointer'}>
                <Flex alignItems={'center'} gap={2} w="full">
                  <Checkbox.Root
                    variant={'solid'}
                    className={styles.catChkbox}
                    checked={map.has(category)}
                    size={'sm'}
                    onClick={(e) => e.stopPropagation()}
                    onCheckedChange={(e) => {
                      onClassChange(e, category);
                    }}
                  >
                    <Checkbox.HiddenInput />
                    <Checkbox.Control
                      className="catChkboxControl"
                      _checked={{ backgroundColor: 'interactive.activated' }}
                    />
                  </Checkbox.Root>
                  <Text fontSize={'sm'} flex="1" fontWeight={'normal'}>
                    {categories[category].label}
                  </Text>
                  <Accordion.ItemIndicator />
                </Flex>
              </Accordion.ItemTrigger>
              <Accordion.ItemContent>
                <Accordion.ItemBody>
                  <Stack gap={4} pl={6}>
                    {getInventoryClass(categories, category).categories.map((subCategory) => (
                      <Flex alignItems={'center'} gap={2} w="full" key={subCategory.category}>
                        <Checkbox.Root
                          borderRadius={'10px !important'}
                          variant={'solid'}
                          size={'sm'}
                          checked={map.get(category)?.includes(subCategory.category) || false}
                          onCheckedChange={(e) => {
                            onCategoryChange(e, category, subCategory.category);
                          }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Checkbox.HiddenInput />
                          <Checkbox.Control
                            className="catChkboxControl"
                            _checked={{ backgroundColor: 'interactive.activated' }}
                          />
                          <Checkbox.Label fontWeight={'normal'} pl={1} cursor={'pointer'}>
                            {subCategory.label}
                          </Checkbox.Label>
                        </Checkbox.Root>
                      </Flex>
                    ))}
                  </Stack>
                </Accordion.ItemBody>
              </Accordion.ItemContent>
            </Accordion.Item>
          ))}
      </Accordion.Root>
    </Stack>
  ) : (
    <Stack>
      {categories &&
        searchParams.inventoryClass &&
        page === LinkEnum.INVENTORY &&
        categories[searchParams.inventoryClass]?.categories.map((category) => (
          <Flex alignItems={'center'} gap={2} w="full" key={category.category}>
            <Checkbox.Root
              variant={'solid'}
              size={'sm'}
              checked={map.get(searchParams.inventoryClass || '')?.includes(category.category) || false}
              onCheckedChange={(e) => {
                onCategoryChange(e, searchParams.inventoryClass || '', category.category);
              }}
              onClick={(e) => e.stopPropagation()}
              colorPalette={'blue'}
            >
              <Checkbox.HiddenInput />
              <Checkbox.Control className="catChkboxControl" _checked={{ backgroundColor: 'interactive.activated' }} />
              <Checkbox.Label fontWeight={'normal'} pl={1} cursor={'pointer'}>
                {category.label}
              </Checkbox.Label>
            </Checkbox.Root>
          </Flex>
        ))}
    </Stack>
  );
};
